import * as d3 from "d3";
import {
  subGroupCssStyle,
  factionCssStyle,
  linkDarkStyle,
  linkLightStyle,
  nodeDarkStyle,
  nodeLightStyle,
  relationCssStyle,
} from "../networkConfig";
import { uuidv4 } from "../../../../../../../common/utils";

function escapeRegExp(string) {
  return string
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/,/g, "\\,")
    .replace(/__/g, "\\__");
}

const drag = (simulation) => {
  function dragstarted(d) {
    if (!d3.event.active) simulation.alphaTarget(0.3).restart();
    // eslint-disable-next-line no-param-reassign
    d.fx = d.x;
    // eslint-disable-next-line no-param-reassign
    d.fy = d.y;
  }

  function dragged(d) {
    // eslint-disable-next-line no-param-reassign
    d.fx = d3.event.x;
    // eslint-disable-next-line no-param-reassign
    d.fy = d3.event.y;
  }

  /** close dragend for user putting node by his own */
  // function dragended(d) {
  // 	if (!d3.event.active) simulation.alphaTarget(0);
  // 	d.fx = null;
  // 	d.fy = null;
  // }

  return d3.drag().on("start", dragstarted).on("drag", dragged);
  // .on('end', dragended);
};

const calculateCurve = (source, target, curveLen) => {
  const mx = (target.x + source.x) * 0.5;
  const my = (target.y + source.y) * 0.5;

  const tsx = target.x - source.x;
  const tsy = target.y - source.y;

  // eslint-disable-next-line no-restricted-properties
  const L = Math.pow(Math.pow(tsx, 2) + Math.pow(tsy, 2), 0.5);

  const qx = mx - (tsy * curveLen) / L;
  const qy = my + (tsx * curveLen) / L;

  return { qx, qy };
};

const getLineCurve = (source, target, curveLen) => {
  const { qx, qy } = calculateCurve(source, target, curveLen);

  // eslint-disable-next-line no-restricted-globals
  if (isNaN(qx)) {
    return `M${source.x}, ${source.y} Q0 0 ${target.x} ${target.y}`;
  }
  return `M${source.x}, ${source.y} Q${qx} ${qy} ${target.x} ${target.y}`;
};

// change nodes display style

/**
 * if nodeCountMeetAuth == false, node.count is equal to link count
 * @param _nodes
 * @param _maxNodes
 * @param _maxLinks
 * @returns {*}
 */
const changeNodesData = (_nodes, _maxNodes = 0, _maxLinks) => {
  if (_maxLinks != null) {
    const nodesModify = _nodes.map((nd) => ({
      ...nd,
      display:
        nd.count >= _maxNodes && nd.count >= _maxLinks ? "true" : "false",
    }));
    return nodesModify;
  }
  const nodesModify = _nodes.map((nd) => ({
    ...nd,
    display: nd.count >= _maxNodes ? "true" : "false",
  }));
  return nodesModify;
};

const linksFilter = (_nodes, _links) => {
  // get nodes id which is displayed
  const nodesIdDisplayed = _nodes
    .filter((n) => n.display === "true")
    .map((nf) => nf.id);
  // modify link display style
  const linksModify = _links.map((l) => ({
    ...l,
    display:
      nodesIdDisplayed.includes(l.id.split("-")[0]) &&
      nodesIdDisplayed.includes(l.id.split("-")[1])
        ? "true"
        : "false",
  }));
  return linksModify;
};

// calculate node's links count
const calcLinksCountOfNode = (_links) => {
  // calculate every node's links count
  const linksCountOfnode = new Map();
  _links.forEach((l) => {
    if (!linksCountOfnode.has(l.source)) {
      linksCountOfnode.set(l.source, 1);
    } else {
      let count = linksCountOfnode.get(l.source);
      count += 1;
      linksCountOfnode.set(l.source, count);
    }
    if (!linksCountOfnode.has(l.target)) {
      linksCountOfnode.set(l.target, 1);
    } else {
      let count = linksCountOfnode.get(l.target);
      count += 1;
      linksCountOfnode.set(l.target, count);
    }
  });
  return linksCountOfnode;
};

const handleMaxNodesMaxLinks = ({
  maxLinks,
  maxNodes,
  links,
  nodes,
  nodeCountMeetAuth,
}) => {
  let nodesModify = [];
  let linksModify = [];
  if (maxLinks == null) {
    // change nodes display style
    nodesModify = changeNodesData(nodes, maxNodes);
    // change links display style
    linksModify = linksFilter(nodesModify, links);
  } else if (nodeCountMeetAuth) {
    // every node's count is meet count with lxt

    // calculate every node's links count: return map object
    // 孤點不會在此清單中
    const linksCountOfNode = calcLinksCountOfNode(links);
    // change nodes display style
    nodesModify = nodes.map((n) => {
      let display = "true";
      if (!(n.count >= maxNodes)) display = "false";
      if (maxLinks != null && maxLinks > 0) {
        // 非孤點
        if (linksCountOfNode.has(n.id)) {
          if (!(linksCountOfNode.get(n.id) >= maxLinks)) {
            display = "false";
          }
        } else {
          // 為孤點,沒有連結
          display = "false";
        }
      }
      return {
        ...n,
        display,
      };
    });
    // change links display style
    linksModify = linksFilter(nodesModify, links);
  } else {
    // change nodes display style
    nodesModify = changeNodesData(nodes, maxNodes, maxLinks);
    // change links display style
    linksModify = linksFilter(nodesModify, links);
  }
  return [linksModify, nodesModify];
};

// change links display
const changeLinksDisplay = (_links) => {
  // change link style
  _links.forEach((l) => {
    d3.select(`[data-id="${escapeRegExp(l.id)}"]`).style(
      "display",
      l.display === "true" ? "inline" : "none"
    );
  });
};

// change nodes display
const changeNodesDisplay = (_nodes) => {
  // change node-circle style
  _nodes.forEach((n) => {
    d3.select(`[data-id=node-circle-${escapeRegExp(n.id)}]`).style(
      "display",
      n.display === "true" ? "inline" : "none"
    );
    d3.select(`[data-id=node-label-${escapeRegExp(n.id)}]`).style(
      "display",
      n.display === "true" ? "inline" : "none"
    );
  });
};

// change background color
const changeBackgroundColor = (backgroundStyle, groupNetwork) => {
  // link style
  const linkStyle = backgroundStyle === "dark" ? linkDarkStyle : linkLightStyle;
  // node style
  const nodeStyle = backgroundStyle === "dark" ? nodeDarkStyle : nodeLightStyle;
  if (!(groupNetwork && groupNetwork.links)) return;
  // link scale color
  const linkColor = d3
    .scaleLinear()
    .domain([
      d3.min(groupNetwork.links, (d) => d.count),
      d3.max(groupNetwork.links, (d) => d.count),
    ])
    .range([`${linkStyle.color.min}`, `${linkStyle.color.max}`]);
  // change all nodes label color
  d3.selectAll(".node-label")
    .attr("fill", `${nodeStyle.strokeColor}`)
    .style("text-shadow", `2px 2px 5px ${nodeStyle.shadowColor}`);
  // change all links color
  d3.selectAll(".link").attr("stroke", (d) => linkColor(d.count));
};

// change faction display
const changeSubGroupDisplay = (curSubGroups, showSubGroup) => {
  if (curSubGroups.length === 0) return;
  // 派系 stroke 樣式
  const subGroupStyle = subGroupCssStyle;
  // change node stroke style
  d3.selectAll(".node-circle").attr("stroke", (d) =>
    // eslint-disable-next-line no-nested-ternary
    !showSubGroup
      ? ""
      : d.subGroup === undefined
      ? ""
      : subGroupStyle.color[curSubGroups.indexOf(d.subGroup)]
  );
  // change faction legend display
  d3.selectAll(".legendSubGroup").style(
    "display",
    showSubGroup ? "inline" : "none"
  );
  d3.selectAll(".legendSubGroupLabel").style(
    "display",
    showSubGroup ? "inline" : "none"
  );
};

// change faction display
const changeFactionDisplay = (factionList, factionDisplayed) => {
  if (factionList.length === 0) return;
  // 派系 stroke 樣式
  const factionStyle = factionCssStyle;
  // change node stroke style
  d3.selectAll(".node-circle").attr("stroke", (d) =>
    // eslint-disable-next-line no-nested-ternary
    !factionDisplayed
      ? ""
      : d.faction === undefined
      ? ""
      : factionStyle.color[factionList.indexOf(d.faction)]
  );
  // change faction legend display
  d3.selectAll(".legendFaction").style(
    "display",
    factionDisplayed ? "inline" : "none"
  );
  d3.selectAll(".legendFactionLabel").style(
    "display",
    factionDisplayed ? "inline" : "none"
  );
};

// change kinship display
const changeKinshipDisplay = (relationList, kinshipDisplayed) => {
  if (relationList.length === 0) return;
  // 派系 stroke 樣式
  const relationStyle = relationCssStyle;
  // change node stroke style
  d3.selectAll(".node-circle").attr("stroke", (d) =>
    // eslint-disable-next-line no-nested-ternary
    !kinshipDisplayed
      ? ""
      : d.relation === undefined
      ? ""
      : relationStyle.color[relationList.indexOf(d.relation)]
  );
  // change relation legend display
  d3.selectAll(".legendRelation").style(
    "display",
    kinshipDisplayed ? "inline" : "none"
  );
  d3.selectAll(".legendRelationLabel").style(
    "display",
    kinshipDisplayed ? "inline" : "none"
  );
};

const generateAllPairs = (members) => {
  const results = [];

  // Since you only want pairs, there's no reason
  // to iterate over the last element directly
  for (let i = 0; i < members.length - 1; i += 1) {
    // This is where you'll capture that last value
    for (let j = i + 1; j < members.length; j += 1) {
      if (members[i] === members[j]) {
        // should not happen here, but it does.
        // eslint-disable-next-line no-continue
        continue;
      }
      results.push({ source: members[i], target: members[j] });
    }
  }
  return results;
};

const safeId = (str) => {
  const newStr = str || "";

  return newStr.replaceAll(" ", "_").replaceAll(".", "_").replaceAll("'", "_");
};

/**
 *
 * @param bindings {{event:string; id:string; label:string; groupName:string; subGroupName: string;}[]}
 * @param nodeCountMeetAuth {boolean}
 * @returns {{nodes: (*&{display: string})[], currentGroups: *[], links: (*&{display: string})[], curSubGroups: *[]}}
 */
const bindingsToNodesLinks = (bindings, nodeCountMeetAuth) => {
  let nodes = [];
  let links = [];
  const currentGroups = []; // bindings 裡有出現的 groups
  const curSubGroups = [];

  const eventObj = {};

  // create nodes and links
  bindings.forEach(({ event, id, label, groupName, subGroupName }) => {
    // get members with group
    if (!(event in eventObj)) {
      eventObj[event] = { members: [] };
    }
    // eventObj[event].members.push(safeId(id));
    eventObj[event].members.push(safeId(label));

    const nodeUniqId = safeId(label);
    const found = nodes.find(({ id: nodeId }) => nodeId === nodeUniqId);
    // const found = nodes.find(({ id: nodeId }) => nodeId === id);
    if (!found) {
      if (nodeCountMeetAuth) {
        nodes.push({
          group: [groupName],
          id: nodeUniqId, // 為了讓使用者上傳的資料可以跟開放日誌的人物互動, id 使用 label(e.g.林獻堂)
          _id: id || "undefined", // _id 為 node uniq id, 保留這個資料方便讀取更多資料
          label, // label 為 node 的顯示名稱
          subGroupName,
          count: 1,
        });
      } else {
        nodes.push({
          group: [groupName],
          id: nodeUniqId,
          _id: id, // _id 為 node uniq id
          label, // label 為 node 的顯示名稱
          subGroupName,
        });
      }
    } else {
      if (nodeCountMeetAuth) {
        found.count += 1;
      }

      if (found.group.indexOf(groupName) < 0) {
        found.group.push(groupName);
      }
      if (!found.subGroup) {
        found.subGroup = subGroupName;
      }
    }

    // for legend
    if (currentGroups.indexOf(groupName) < 0) {
      currentGroups.push(groupName);
    }
    if (subGroupName && curSubGroups.indexOf(subGroupName) < 0) {
      curSubGroups.push(subGroupName);
    }
  });
  curSubGroups.sort();

  let allLinks = [];
  // create member matrix
  Object.keys(eventObj).forEach((key) => {
    // eslint-disable-next-line prefer-destructuring
    const members = Array.from(new Set(eventObj[key].members)).sort();

    if (members.length > 1) {
      const pairs = generateAllPairs(members);

      // increase count of the link.
      allLinks = allLinks.concat(pairs);
    }
  });
  // count the links
  // link.source <==> link.target
  allLinks.forEach((al) => {
    const found = links.find(
      (l) =>
        (l.source === al.source && l.target === al.target) ||
        (l.source === al.target && l.target === al.source)
    );
    if (!found) {
      links.push({
        id: `${al.source}-${al.target}`,
        source: al.source,
        target: al.target,
        count: 1,
      });
    } else {
      found.count += 1;
    }
  });

  if (!nodeCountMeetAuth) {
    // To calculate total link number as node number.
    nodes.forEach((node) => {
      // eslint-disable-next-line no-param-reassign
      node.count = links
        .map((l) => {
          if (l.source === node.id || l.target === node.id) {
            return l.count;
          }
          return 0;
        })
        .reduce((prev, ele) => prev + ele, 0);
    });
  }

  // set all links' display style to true when data change
  links = links.map((li) => ({
    ...li,
    display: "true",
  }));
  // set all nodes' display style to true when data change
  nodes = nodes.map((nd) => ({
    ...nd,
    display: "true",
  }));

  return {
    nodes,
    links,
    currentGroups,
    curSubGroups,
  };
};

// nodes 增加一個屬性 centered: true/false
const addCenteredKey = (nodes) => {
  // eslint-disable-next-line no-underscore-dangle
  const _ndoes = Object.assign([], nodes);
  // 取得 node.count 的中位數
  const nodeCountMedian = d3.median(nodes, (d) => d.count);
  return _ndoes.map((n) => ({
    ...n,
    centered: n.count >= nodeCountMedian,
  }));
};

// relData:
// [{
//   datasetId:
//   datasetName:
//   srcId:
//   srcLabel:
//   dstId:
//   dstLabel:
// },{},...]

// return [{
//   event: randomId,
//   id: item.person, // node uniq id
//   label: item.personName, // node label
//   group: item.datasetGraph,
//   groupName: item.datasetName,
// },{},...]
const relationToNodes = (relData = []) => {
  const nodes = [];

  relData.forEach((dt, idx) => {
    const eventId = `evt-${uuidv4()}`;
    const {
      datasetId,
      datasetName,
      srcId,
      srcLabel,
      dstId,
      dstLabel,
      group,
      groupName,
      eventType,
      eventTypeName,
    } = dt;
    nodes.push({
      event: eventId,
      id: srcId,
      label: srcLabel,
      group: datasetId,
      groupName: datasetName,
      eventType,
      eventTypeName,
    });
    nodes.push({
      event: eventId,
      id: dstId,
      label: dstLabel,
      group: datasetId || group,
      groupName: datasetName || groupName,
      eventType,
      eventTypeName,
    });
  });
  return nodes;
};

export {
  drag,
  getLineCurve,
  handleMaxNodesMaxLinks,
  changeLinksDisplay,
  changeNodesDisplay,
  changeBackgroundColor,
  changeSubGroupDisplay,
  bindingsToNodesLinks,
  addCenteredKey,
  escapeRegExp,
  relationToNodes,
};
